import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Upload,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
  Database,
  Search,
  RefreshCw,
  ChevronDown,
  Download,
  Filter,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  Layers,
  Grid,
  Zap,
  AlertCircle,
} from "lucide-react";
import ErrorColumnAnalysis from "./ErrorColumnAnalysis";
import FailedColumnDetails from "./FailedColumnDetails";
import DimensionCards from "./DimensionCards";
import MetricsCards from "./MetricsCards";
import DimensionDistribution from "./DimensionDistribution";
import ColumnAnalysisTable from "./ColumnAnalysisTable";
import DimensionErrorAnalysis from "./DimensionErrorAnalysis";
import ColumnHeatMap from "./ColumnHeatMap";
import Recommendations from "./Recommendations";

const API_BASE_URL = "https://osapi.bluegecko.ai:8003";

const DataQualityDashboard = () => {
  // State Management
  const [domains, setDomains] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tables, setTables] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [schemaTables, setSchemaTables] = useState([]);

  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [selectedTables, setSelectedTables] = useState([]);
  const [analysisMode, setAnalysisMode] = useState("file"); // file, table, database

  const [files, setFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [error, setError] = useState(null);
  // Add this state near your other state declarations (around line 50)
  const [selectedDimensionDetail, setSelectedDimensionDetail] = useState(null);
  const [selectedPieChartDimension, setSelectedPieChartDimension] =
    useState(null);
  const [showDimensionDetailModal, setShowDimensionDetailModal] =
    useState(false);

  const [isDomainDropdownOpen, setIsDomainDropdownOpen] = useState(false);
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const [isSchemaDropdownOpen, setIsSchemaDropdownOpen] = useState(false);
  const [isTableDropdownOpen, setIsTableDropdownOpen] = useState(false);
  const [showRecommendationsModal, setShowRecommendationsModal] =
    useState(false);

  const [searchDomain, setSearchDomain] = useState("");
  const [searchTemplate, setSearchTemplate] = useState("");
  const [searchSchema, setSearchSchema] = useState("");
  const [searchTable, setSearchTable] = useState("");
  // Add after existing state declarations
  const [objects, setObjects] = useState([]);
  const [selectedObject, setSelectedObject] = useState(null);
  const [isObjectDropdownOpen, setIsObjectDropdownOpen] = useState(false);
  const [searchObject, setSearchObject] = useState("");

  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [multiFileResults, setMultiFileResults] = useState([]);
  const [expandedDimension, setExpandedDimension] = useState(null);
  const [hoveredDimension, setHoveredDimension] = useState(null);

  // Pagination states
  const [detailsPageSize, setDetailsPageSize] = useState(10);
  const [currentDetailsPage, setCurrentDetailsPage] = useState(1);

  // Fetch Domains on Mount
  useEffect(() => {
    fetchDomains();
    fetchSchemas();
  }, []);

  // API Calls
  const fetchDomains = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/domains`);
      const data = await response.json();
      setDomains(data.domains || []);
    } catch (err) {
      console.error("Error fetching domains:", err);
    }
  };

  const fetchTemplates = async (domain) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/domains/${domain}/templates`
      );
      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (err) {
      console.error("Error fetching templates:", err);
    }
  };

  // MODIFY existing fetchTables function
  const fetchTables = async (domain, template) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/domains/${domain}/templates/${template}/tables`
      );
      const data = await response.json();
      setObjects(data.objects || []); // CHANGE: Use setObjects instead of setTables
    } catch (err) {
      console.error("Error fetching objects:", err);
    }
  };

  const fetchSchemas = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/database/schemas`);
      const data = await response.json();
      setSchemas(data.schemas || []);
    } catch (err) {
      console.error("Error fetching schemas:", err);
    }
  };

  const fetchSchemaTablesData = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/database/schemas/doc/tables`
      ); // CHANGE: Hardcode "doc"
      const data = await response.json();
      setSchemaTables(data.tables || []);
    } catch (err) {
      console.error("Error fetching schema tables:", err);
      setError("Failed to fetch tables from database");
    }
  };

  const analyzeFiles = async () => {
    setIsAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();

      if (selectedDomain) formData.append("domain", selectedDomain);
      if (selectedTemplate) formData.append("template", selectedTemplate);
      if (selectedObject) formData.append("object_name", selectedObject);

      files.forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetch(`${API_BASE_URL}/analyze-file`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      // Check if this is a multi-file response
     if (data.analysis_type === "multi_file") {
  // Parse multi-file response - group data by filename
  const fileNames = new Set();
  const combinedMetadata =
    data.sections?.["3_combined_metadata_table"] || [];

  // Get unique filenames
  combinedMetadata.forEach((row) => {
    if (row.filename) fileNames.add(row.filename);
  });

  // Create results for each file
  const multiResults = Array.from(fileNames).map((fileName) => {
    // Filter data for this specific file
    const fileMetadata = combinedMetadata.filter(
      (row) => row.filename === fileName
    );

    // Group failed columns analysis by dimension for this file
    const fileFailedColumnsData = (
      data.sections?.["4_graphs"]?.failed_columns_analysis || []
    ).filter(col => col.filename === fileName);

    // Group by dimension
    const dimensionMap = {};
    fileFailedColumnsData.forEach(col => {
      if (!dimensionMap[col.dimension]) {
        dimensionMap[col.dimension] = {
          dimension: col.dimension,
          columns: []
        };
      }
      dimensionMap[col.dimension].columns.push({
        column: col.column,
        failed: col.failed,
        total_records: col.total_records,
        success_rate: col.success_rate,
        filename: col.filename
      });
    });

    const fileFailedColumnsAnalysis = Object.values(dimensionMap);
    
    return transformApiResponseForFile(
      data,
      fileMetadata,
      fileName,
      fileFailedColumnsAnalysis
    );
  });

  // CREATE COMBINED SUMMARY using API's aggregated data (sections 1 & 2)
  const combinedSummary = {
    filename: "All Files Combined",
    analysis_type: "multi_file",
    summary: {
      overall_quality_score: data.sections?.["1_overall_summary"]?.overall_quality || 0,
      total_records: data.sections?.["1_overall_summary"]?.total_records || 0,
      total_issues: data.sections?.["1_overall_summary"]?.records_failed || 0,
      total_columns: new Set(combinedMetadata.map(row => row.Column)).size,
      columns_with_issues: combinedMetadata.filter(row => row.Failed > 0).length,
      dimension_details: (data.sections?.["2_dimension_cards"] || []).map(dim => ({
        dimension: dim.dimension,
        success_rate: parseFloat(dim.percentage),
        passed: dim.passed,
        failed: dim.failed,
        total: dim.total,
        percentage: parseFloat(dim.percentage),
        status: dim.percentage >= 95 ? "Excellent" : 
                dim.percentage >= 80 ? "Good" : 
                dim.percentage >= 60 ? "Needs Attention" : "Critical",
        columns: [...new Set(combinedMetadata
          .filter(row => row.Dimension === dim.dimension)
          .map(row => row.Column))],
        failed_columns: combinedMetadata
          .filter(row => row.Dimension === dim.dimension && row.Failed > 0)
          .map(col => ({
            column: col.Column,
            failed: col.Failed,
            total: col.Total_Validations,
            success_rate: col.Success_Rate,
            rule: col.Rule,
            status: col.Status,
          }))
      }))
    },
    // Add empty arrays for other sections since this is just for metrics/dimensions
    visualizations: { quality_by_dimension: [], issue_distribution: [] },
    details: [],
    insights: [],
    recommendations: [],
    column_statistics: [],
    statistics: {
      total_validations: data.sections?.["1_overall_summary"]?.total_records || 0,
      passed_validations: data.sections?.["1_overall_summary"]?.records_passed || 0,
      failed_validations: data.sections?.["1_overall_summary"]?.records_failed || 0,
      data_quality_index: data.sections?.["1_overall_summary"]?.overall_quality || 0,
    },
    failed_columns_analysis: [],
    dimension_details_with_fixes: []
  };

  setMultiFileResults([combinedSummary, ...multiResults]);
  setAnalysisResults(combinedSummary); // Show combined summary first
  setCurrentFileIndex(0);
  setCurrentDetailsPage(1);
} else {
        // Single file response
        const transformedData = transformApiResponse(data);
        setAnalysisResults(transformedData);
        setMultiFileResults([transformedData]);
        setCurrentFileIndex(0);
        setCurrentDetailsPage(1);
      }
    } catch (err) {
      setError(err.message);
      console.error("Error analyzing files:", err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const transformApiResponseForFile = (
    fullData,
    fileMetadata,
    fileName,
    fileFailedColumnsAnalysis
  ) => {
    // Calculate per-file statistics from the metadata
    const fileSummary = {
      total_records: fileMetadata.reduce(
        (sum, row) => sum + (row.Total_Validations || 0),
        0
      ),
      passed: fileMetadata.reduce((sum, row) => sum + (row.Passed || 0), 0),
      failed: fileMetadata.reduce((sum, row) => sum + (row.Failed || 0), 0),
    };

    const overall_quality =
      fileSummary.total_records > 0
        ? ((fileSummary.passed / fileSummary.total_records) * 100).toFixed(2)
        : 0;

    // Get detailed fixes from section 5 for this file
    const fileDetailsWithFixes = (
      fullData.sections?.["5_file_details_with_fixes"] || []
    ).find((detail) => detail.filename === fileName);

    // Group metadata by dimension to calculate per-dimension stats
    const dimensionStats = {};
    fileMetadata.forEach((row) => {
      if (!dimensionStats[row.Dimension]) {
        dimensionStats[row.Dimension] = {
          dimension: row.Dimension,
          passed: 0,
          failed: 0,
          total: 0,
          percentage: 0,
          columns: [], // Initialize columns array
          failed_columns: [], // Add failed columns tracking
        };
      }
      dimensionStats[row.Dimension].passed += row.Passed || 0;
      dimensionStats[row.Dimension].failed += row.Failed || 0;
      dimensionStats[row.Dimension].total += row.Total_Validations || 0;

      // Add column to the columns array if it's not already there
      if (!dimensionStats[row.Dimension].columns.includes(row.Column)) {
        dimensionStats[row.Dimension].columns.push(row.Column);
      }

      // Track failed columns
      if (row.Failed > 0) {
        // Find detailed info for this column from section 5
        let detailedColumnInfo = {
          column: row.Column,
          failed: row.Failed,
          total: row.Total_Validations,
          success_rate: row.Success_Rate,
          rule: row.Rule,
          status: row.Status,
        };

        // If we have detailed fixes, enrich the column info
        if (
          fileDetailsWithFixes &&
          fileDetailsWithFixes.dimensions_with_issues
        ) {
          const dimIssue = fileDetailsWithFixes.dimensions_with_issues.find(
            (di) => di.dimension === row.Dimension
          );
          if (dimIssue && dimIssue.failed_columns) {
            const detailedCol = dimIssue.failed_columns.find(
              (fc) => fc.column === row.Column
            );
            if (detailedCol) {
              detailedColumnInfo = {
                ...detailedColumnInfo,
                ...detailedCol, // Merge all detailed info (sql_fix, python_fix, ai_insight, failed_sample_details, etc.)
              };
            }
          }
        }

        dimensionStats[row.Dimension].failed_columns.push(detailedColumnInfo);
      }
    });

    // NOW calculate percentages and status AFTER dimensionStats is populated
    Object.values(dimensionStats).forEach((stat) => {
      stat.percentage =
        stat.total > 0 ? ((stat.passed / stat.total) * 100).toFixed(2) : 0;
      stat.success_rate = stat.percentage;

      // ADD STATUS LOGIC
      const percentage = parseFloat(stat.percentage);
      if (percentage >= 95) {
        stat.status = "Excellent";
      } else if (percentage >= 80) {
        stat.status = "Good";
      } else if (percentage >= 60) {
        stat.status = "Needs Attention";
      } else {
        stat.status = "Critical";
      }
    });

    return {
      filename: fileName,
      analysis_type: fullData.analysis_type,
      summary: {
        overall_quality_score: parseFloat(overall_quality),
        total_records: fileSummary.total_records,
        total_issues: fileSummary.failed,
        total_columns: new Set(fileMetadata.map((row) => row.Column)).size,
        columns_with_issues: fileMetadata.filter((row) => row.Failed > 0)
          .length,
        dimension_details: Object.values(dimensionStats).map((stat) => ({
          dimension: stat.dimension,
          success_rate: parseFloat(stat.success_rate),
          passed: stat.passed,
          failed: stat.failed,
          total: stat.total,
          percentage: parseFloat(stat.percentage),
          failed_columns: stat.failed_columns,
          columns: stat.columns,
        })),
      },

      visualizations: {
        quality_by_dimension: Object.values(dimensionStats).map((stat) => ({
          dimension: stat.dimension,
          score: stat.percentage,
          passed: stat.passed,
          failed: stat.failed,
        })),

        issue_distribution: Object.values(dimensionStats).map((stat) => ({
          name: stat.dimension,
          value: stat.failed,
          total: stat.total,
          passed: stat.passed,
          columns: stat.columns || [],
        })),
      },

      details: fileMetadata.map((row) => ({
        column: row.Column,
        dimension: row.Dimension,
        rule: row.Rule,
        success_rate: row.Success_Rate,
        total_records: row.Total_Validations,
        success_count: row.Passed,
        fail_count: row.Failed,
        status: row.Status,
        filename: row.filename,
      })),

      insights:
        fileMetadata
          .filter((row) => row.Failed > 0)
          .map((row) => ({
            title: `${row.Column} - ${row.Dimension} Issue`,
            message: `${row.Column} has ${row.Failed} failures`,
            priority:
              row.Success_Rate < 50
                ? "high"
                : row.Success_Rate < 80
                ? "medium"
                : "low",
            severity:
              row.Success_Rate < 50
                ? "high"
                : row.Success_Rate < 80
                ? "medium"
                : "low",
            recommendation: `Success Rate: ${row.Success_Rate}% - Review failures`,
          })) || [],

      recommendations:
        (() => {
          const insights = fullData.sections?.["6_ai_insights"];
          if (!insights) return [];

          // Split by numbered points (1., 2., 3., etc.) or bullet points
          const sections = insights.split(/\n(?=\d+\.\s)/);

          return sections
            .filter((section) => section.trim())
            .map((section, idx) => {
              // Remove leading number and clean up
              const cleaned = section.replace(/^\d+\.\s*/, "").trim();

              // Split into lines and extract the main point and details
              const lines = cleaned.split("\n").filter((line) => line.trim());
              const mainPoint = lines[0];

              // Extract sub-points (lines starting with - or bullet points)
              const details = lines
                .slice(1)
                .filter((line) => line.trim().match(/^[-•]/))
                .map((line) => line.replace(/^[-•]\s*/, "").trim())
                .join("\n");

              return {
                title: `Insight ${idx + 1}`,
                action: mainPoint,
                description: details || mainPoint,
              };
            });
        })() || [],

      column_statistics: Array.from(
        fileMetadata
          .reduce((map, row) => {
            if (!map.has(row.Column)) {
              map.set(row.Column, {
                column_name: row.Column,
                completeness: 0,
                uniqueness: 0,
                validity: 0,
              });
            }
            const stat = map.get(row.Column);
            if (row.Dimension === "Completeness")
              stat.completeness = row.Success_Rate;
            if (row.Dimension === "Uniqueness")
              stat.uniqueness = row.Success_Rate;
            if (row.Dimension === "Validity") stat.validity = row.Success_Rate;
            return map;
          }, new Map())
          .values()
      ),

      statistics: {
        total_validations: fileSummary.total_records,
        passed_validations: fileSummary.passed,
        failed_validations: fileSummary.failed,
        data_quality_index: parseFloat(overall_quality),
      },

      failed_columns_analysis: fileFailedColumnsAnalysis.map((dimAnalysis) => ({
        dimension: dimAnalysis.dimension,
        columns: dimAnalysis.columns || [],
      })),
      dimension_details_with_fixes: (
        fullData.sections?.["5_file_details_with_fixes"] || []
      ).filter(
        (detail) =>
          detail.filename === fileName ||
          detail.filename === fileName.replace(/\s+/g, " ")
      ),
    };
  };

  const transformApiResponse = (data) => {
    // Extract dimension details from the 2_dimension_cards section
    const dimensionCardsData = data.sections?.["2_dimension_cards"] || [];
    const metadataTable =
      data.sections?.["3_metadata_table"] ||
      data.sections?.["3_combined_metadata_table"] ||
      [];

    // Build dimension details from dimension cards
    const dimensionDetailsFromCards = dimensionCardsData.map((dim) => {
      // Get full dimension details from section 5
      const fullDimensionDetail = (
        data.sections?.["5_dimension_details_with_fixes"] || []
      ).find((detail) => detail.dimension === dim.dimension);

      // Find failed columns for this dimension from metadata
      const failedColumnsForDim = metadataTable.filter(
        (row) => row.Dimension === dim.dimension && row.Failed > 0
      );

      return {
        dimension: dim.dimension,
        success_rate: parseFloat(dim.percentage),
        passed: dim.passed,
        failed: dim.failed,
        total: dim.total,
        percentage: parseFloat(dim.percentage),
        status: dim.status, // ADD THIS LINE
        // Use full details from API if available, otherwise use metadata
        failed_columns:
          fullDimensionDetail?.failed_columns ||
          failedColumnsForDim.map((col) => ({
            column: col.Column,
            failed: col.Failed,
            total: col.Total_Validations,
            success_rate: col.Success_Rate,
            rule: col.Rule,
            status: col.Status,
          })),
        columns: [
          ...new Set(
            metadataTable
              .filter((row) => row.Dimension === dim.dimension)
              .map((row) => row.Column)
          ),
        ],
      };
    });

    return {
      filename: data.filename || "Analysis Result",
      analysis_type: data.analysis_type,
      summary: {
        overall_quality_score:
          data.sections?.["1_overall_summary"]?.overall_quality || 0,
        total_records: data.sections?.["1_overall_summary"]?.total_records || 0,
        total_issues: data.sections?.["1_overall_summary"]?.records_failed || 0,
        total_columns:
          (
            data.sections?.["3_metadata_table"] ||
            data.sections?.["3_combined_metadata_table"]
          )?.length || 0,
        columns_with_issues:
          (
            data.sections?.["3_metadata_table"] ||
            data.sections?.["3_combined_metadata_table"]
          )?.filter((col) => col.Failed > 0).length || 0,
        dimension_details: dimensionDetailsFromCards,
      },

      // ADD THIS AS A SEPARATE TOP-LEVEL PROPERTY:
      dimension_details:
        data.sections?.["5_dimension_details_with_fixes"] || [],

      visualizations: {
        quality_by_dimension:
          data.sections?.["2_dimension_cards"]?.map((dim) => ({
            dimension: dim.dimension,
            score: dim.percentage,
            passed: dim.passed,
            failed: dim.failed,
          })) || [],

        issue_distribution: (() => {
          // Build a map of dimensions to their columns from metadata
          const metadataTable =
            data.sections?.["3_metadata_table"] ||
            data.sections?.["3_combined_metadata_table"] ||
            [];
          const dimensionColumns = {};

          metadataTable.forEach((row) => {
            if (!dimensionColumns[row.Dimension]) {
              dimensionColumns[row.Dimension] = [];
            }
            if (!dimensionColumns[row.Dimension].includes(row.Column)) {
              dimensionColumns[row.Dimension].push(row.Column);
            }
          });

          // Map dimension cards with their columns
          return (data.sections?.["2_dimension_cards"] || []).map((dim) => ({
            name: dim.dimension,
            value: dim.failed,
            total: dim.total || 0,
            passed: dim.passed || 0,
            percentage: dim.completeness_percentage || 0,
            columns: dimensionColumns[dim.dimension] || [],
          }));
        })(),
      },

      details:
        (
          data.sections?.["3_metadata_table"] ||
          data.sections?.["3_combined_metadata_table"]
        )?.map((row) => ({
          column: row.Column,
          dimension: row.Dimension,
          rule: row.Rule,
          success_rate: row.Success_Rate,
          total_records: row.Total_Validations,
          success_count: row.Passed,
          fail_count: row.Failed,
          status: row.Status,
        })) || [],

      insights:
        data.sections?.["5_dimension_details_with_fixes"]
          ?.flatMap((dim) =>
            dim.failed_columns?.map((col) => ({
              title: `${col.column} - ${dim.dimension} Issue`,
              message: `${col.column} has ${col.failed} failures`,
              description: col.ai_insight,
              priority:
                col.success_rate < 50
                  ? "high"
                  : col.success_rate < 80
                  ? "medium"
                  : "low",
              severity:
                col.success_rate < 50
                  ? "high"
                  : col.success_rate < 80
                  ? "medium"
                  : "low",
              recommendation: `Success Rate: ${col.success_rate}% - Review failures`,
            }))
          )
          .filter(Boolean) || [],

      recommendations:
        (() => {
          const insights = data.sections?.["6_ai_insights"];
          if (!insights) return [];

          // Split by numbered points (1., 2., 3., etc.)
          const sections = insights.split(/\n(?=\d+\.\s)/);

          return sections
            .filter((section) => section.trim())
            .map((section, idx) => {
              // Remove leading number and clean up
              const cleaned = section.replace(/^\d+\.\s*/, "").trim();

              // Split into lines and extract the main point and details
              const lines = cleaned.split("\n").filter((line) => line.trim());
              const mainPoint = lines[0];

              // Extract sub-points (lines starting with - or bullet points)
              const details = lines
                .slice(1)
                .filter((line) => line.trim().match(/^[-•]/))
                .map((line) => line.replace(/^[-•]\s*/, "").trim())
                .join("\n");

              return {
                title: `Insight ${idx + 1}`,
                action: mainPoint,
                description: details || mainPoint,
              };
            });
        })() || [],

      column_statistics: Array.from(
        (
          data.sections?.["3_metadata_table"] ||
          data.sections?.["3_combined_metadata_table"]
        ) // CHANGE this line
          ?.reduce((map, row) => {
            if (!map.has(row.Column)) {
              map.set(row.Column, {
                column_name: row.Column,
                completeness: 0,
                uniqueness: 0,
                validity: 0,
              });
            }
            const stat = map.get(row.Column);
            if (row.Dimension === "Completeness")
              stat.completeness = row.Success_Rate;
            if (row.Dimension === "Uniqueness")
              stat.uniqueness = row.Success_Rate;
            if (row.Dimension === "Validity") stat.validity = row.Success_Rate;
            return map;
          }, new Map())
          .values() || []
      ),

      statistics: {
        total_validations:
          data.sections?.["1_overall_summary"]?.total_records || 0,
        passed_validations:
          data.sections?.["1_overall_summary"]?.records_passed || 0,
        failed_validations:
          data.sections?.["1_overall_summary"]?.records_failed || 0,
        data_quality_index:
          data.sections?.["1_overall_summary"]?.overall_quality || 0,
      },

      // Add failed columns analysis for ErrorColumnAnalysis component
      failed_columns_analysis:
        data.sections?.["4_graphs"]?.failed_columns_analysis || [],

      // Add dimension details with fixes for FailedColumnDetails component
      dimension_details_with_fixes:
        data.sections?.["5_dimension_details_with_fixes"] || [],
    };
  };

  const analyzeTables = async () => {
    setIsAnalyzing(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append("db_schema", "doc"); // CHANGE: Hardcode to "doc"
      params.append("tables", selectedTables.join(",")); // This is correct

      // CHANGE: Add required fields
      if (selectedDomain) params.append("domain", selectedDomain);
      if (selectedTemplate) params.append("template", selectedTemplate);
      if (selectedObject) params.append("object_name", selectedObject); // ADD: Required field

      const response = await fetch(`${API_BASE_URL}/analyze-table`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorData}`
        );
      }

      const data = await response.json();
      const transformedData = transformApiResponse(data);

      setAnalysisResults(transformedData);
      setMultiFileResults([transformedData]);
    } catch (err) {
      setError(err.message);
      console.error("Error analyzing tables:", err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const analyzeDatabase = async () => {
    setIsAnalyzing(true);
    setError(null);

    try {
      const params = new URLSearchParams();

      // CHANGE: Use selectedSchema if available, otherwise default to "doc"
      params.append("db_schema", selectedSchema || "doc");

      // CHANGE: Make domain and template required
      if (!selectedDomain || !selectedTemplate) {
        throw new Error(
          "Domain and Template are required for database analysis"
        );
      }

      params.append("domain", selectedDomain);
      params.append("template", selectedTemplate);

      const response = await fetch(`${API_BASE_URL}/analyze-database`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorData}`
        );
      }

      const data = await response.json();

      // CHANGE: Use transformApiResponse for consistency
      const transformedData = transformApiResponse(data);

      setAnalysisResults(transformedData);
      setMultiFileResults([transformedData]);
    } catch (err) {
      setError(err.message);
      console.error("Error analyzing database:", err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAnalyze = () => {
    if (analysisMode === "file") analyzeFiles();
    else if (analysisMode === "table") analyzeTables();
    else if (analysisMode === "database") analyzeDatabase();
  };

  const handleFileUpload = (event) => {
    const uploadedFiles = Array.from(event.target.files);
    setFiles(uploadedFiles);
    setAnalysisResults(null);
    setMultiFileResults([]);
  };

  const handleDomainSelect = (domain) => {
    setSelectedDomain(domain);
    setSelectedTemplate(null);
    setTables([]);
    setIsDomainDropdownOpen(false);
    fetchTemplates(domain);
  };

  // MODIFY existing function
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setSelectedObject(null);
    setIsTemplateDropdownOpen(false);

    if (selectedDomain) {
      fetchTables(selectedDomain, template);
    }

    // CHANGE: Auto-fetch database tables for table analysis mode
    if (analysisMode === "table") {
      fetchSchemaTablesData();
    }

    // CHANGE: Auto-set schema to "doc" for database mode
    if (analysisMode === "database") {
      setSelectedSchema("doc");
    }
  };

  // ADD new function after handleTemplateSelect
  const handleObjectSelect = (object) => {
    setSelectedObject(object);
    setIsObjectDropdownOpen(false);
  };

  const handleSchemaSelect = (schema) => {
    setSelectedSchema(schema);
    setIsSchemaDropdownOpen(false);
    fetchSchemaTablesData(schema);
  };

  const toggleTableSelection = (table) => {
    setSelectedTables((prev) =>
      prev.includes(table) ? prev.filter((t) => t !== table) : [...prev, table]
    );
  };

  const getQualityColor = (score) => {
    const numScore =
      typeof score === "string"
        ? parseFloat(score.replace("%", ""))
        : parseFloat(score);
    if (numScore >= 90) return "#10b981";
    if (numScore >= 70) return "#f59e0b";
    return "#ef4444";
  };

  const COLORS = [
    "#ef4444",
    "#f59e0b",
    "#3b82f6",
    "#10b981",
    "#8b5cf6",
    "#ec4899",
    "#14b8a6",
  ];

  // Pagination helpers
  const getPaginatedDetails = () => {
    if (!analysisResults || !analysisResults.details) return [];
    const startIndex = (currentDetailsPage - 1) * detailsPageSize;
    const endIndex = startIndex + detailsPageSize;
    return analysisResults.details.slice(startIndex, endIndex);
  };

  const getTotalDetailsPages = () => {
    if (!analysisResults || !analysisResults.details) return 1;
    return Math.ceil(analysisResults.details.length / detailsPageSize);
  };

  const handleChangeFile = (newIndex) => {
    setCurrentFileIndex(newIndex);
    setAnalysisResults(multiFileResults[newIndex]);
    setCurrentDetailsPage(1); // Reset pagination when changing files
  };

  const canAnalyze = () => {
    if (analysisMode === "file") {
      return (
        files.length > 0 && selectedDomain && selectedTemplate && selectedObject
      );
    }
    if (analysisMode === "table") {
      return (
        selectedDomain &&
        selectedTemplate &&
        selectedObject &&
        selectedTables.length > 0
      );
    }
    if (analysisMode === "database") {
      // CHANGE: Only require domain and template (schema defaults to "doc")
      return selectedDomain && selectedTemplate;
    }
    return false;
  };

  const DropdownSelect = ({
    label,
    value,
    options,
    onSelect,
    isOpen,
    setIsOpen,
    searchTerm,
    setSearchTerm,
    icon: Icon,
  }) => (
    <div className="relative">
      <label className="block text-xs font-bold text-gray-600 mb-2 uppercase tracking-wider">
        {label}
      </label>
      <div
        className="flex items-center justify-between px-4 py-3 bg-white rounded-xl border-2 border-gray-200 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all duration-200"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-gray-700">
            {value || "Select..."}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {isOpen && (
        <div className="absolute top-full mt-2 w-full bg-white rounded-xl border-2 border-gray-200 shadow-2xl z-50 overflow-hidden">
          <div className="p-3 border-b border-gray-100 bg-gray-50">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {options
              .filter((opt) =>
                opt.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((option, idx) => (
                <div
                  key={idx}
                  className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 transition-colors last:border-0 group"
                  onClick={() => {
                    onSelect(option);
                    setSearchTerm("");
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full group-hover:scale-150 transition-transform"></div>
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {option}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      <div className="w-full px-6 py-6 space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl px-5 py-4 shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/15 rounded-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>

              <div>
                <h1 className="text-lg font-semibold text-white leading-tight">
                  Data Quality Hub
                </h1>
                <p className="text-xs text-blue-100">
                  Analytics & data insights
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Mode Selector */}
        <div className="bg-white rounded-xl px-5 py-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-900">
              Analysis Mode
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[
              {
                id: "file",
                label: "File Upload",
                icon: Upload,
                desc: "Uploaded files",
              },
              {
                id: "table",
                label: "Table Analysis",
                icon: Grid,
                desc: "Specific tables",
              },
              {
                id: "database",
                label: "Database Scan",
                icon: Database,
                desc: "Full database",
              },
            ].map((mode) => (
              <button
                key={mode.id}
                onClick={() => {
                  setAnalysisMode(mode.id);
                  setAnalysisResults(null);
                  setMultiFileResults([]);
                }}
                className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border transition ${
                  analysisMode === mode.id
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-blue-300"
                }`}
              >
                <mode.icon
                  className={`w-5 h-5 ${
                    analysisMode === mode.id ? "text-blue-600" : "text-gray-400"
                  }`}
                />

                <span
                  className={`text-xs font-medium ${
                    analysisMode === mode.id ? "text-blue-900" : "text-gray-700"
                  }`}
                >
                  {mode.label}
                </span>

                <span className="text-[11px] text-gray-500 text-center leading-tight">
                  {mode.desc}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Configuration Panel */}
        <div className="bg-white rounded-xl px-5 py-4 border border-gray-200 shadow-sm">
          {/* Header */}
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-900">
              Configuration
            </h2>
          </div>

          {/* Selectors */}
          <div className="grid grid-cols-5 gap-3 mb-4">
            <DropdownSelect
              label="Domain"
              value={selectedDomain}
              options={domains}
              onSelect={handleDomainSelect}
              isOpen={isDomainDropdownOpen}
              setIsOpen={setIsDomainDropdownOpen}
              searchTerm={searchDomain}
              setSearchTerm={setSearchDomain}
              icon={Database}
            />

            {selectedDomain && (
              <DropdownSelect
                label="Template"
                value={selectedTemplate}
                options={templates}
                onSelect={handleTemplateSelect}
                isOpen={isTemplateDropdownOpen}
                setIsOpen={setIsTemplateDropdownOpen}
                searchTerm={searchTemplate}
                setSearchTerm={setSearchTemplate}
                icon={FileSpreadsheet}
              />
            )}

            {/* Object dropdown only for file & table modes */}
            {selectedTemplate &&
              objects.length > 0 &&
              analysisMode !== "database" && (
                <DropdownSelect
                  label="Object Name"
                  value={selectedObject}
                  options={objects}
                  onSelect={handleObjectSelect}
                  isOpen={isObjectDropdownOpen}
                  setIsOpen={setIsObjectDropdownOpen}
                  searchTerm={searchObject}
                  setSearchTerm={setSearchObject}
                  icon={Grid}
                />
              )}

            {/* Schema dropdown only for database mode */}
            {analysisMode === "database" && selectedTemplate && (
              <DropdownSelect
                label="Schema"
                value={selectedSchema || "doc"}
                options={schemas}
                onSelect={handleSchemaSelect}
                isOpen={isSchemaDropdownOpen}
                setIsOpen={setIsSchemaDropdownOpen}
                searchTerm={searchSchema}
                setSearchTerm={setSearchSchema}
                icon={Layers}
              />
            )}

            {/* Table selector – table mode only */}
            {analysisMode === "table" && selectedObject && (
              <div className="relative">
                <label className="block text-[11px] font-medium text-gray-600 mb-1 uppercase tracking-wide">
                  Tables
                </label>

                <div
                  className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-gray-300 cursor-pointer hover:border-blue-400 transition"
                  onClick={() => setIsTableDropdownOpen(!isTableDropdownOpen)}
                >
                  <div className="flex items-center gap-2">
                    <Grid className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-medium text-gray-700">
                      {selectedTables.length > 0
                        ? `${selectedTables.length} selected`
                        : "Select tables"}
                    </span>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform ${
                      isTableDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>

                {isTableDropdownOpen && (
                  <div className="absolute top-full mt-1.5 w-full bg-white rounded-lg border border-gray-200 shadow-lg z-50">
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
                        <input
                          type="text"
                          placeholder="Search tables"
                          className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          value={searchTable}
                          onChange={(e) => setSearchTable(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>

                    <div className="max-h-52 overflow-y-auto p-1.5">
                      {schemaTables
                        .filter((t) =>
                          t.toLowerCase().includes(searchTable.toLowerCase())
                        )
                        .map((table, idx) => (
                          <label
                            key={idx}
                            className="flex items-center gap-2 px-2 py-1.5 hover:bg-blue-50 rounded-md cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedTables.includes(table)}
                              onChange={() => toggleTableSelection(table)}
                              className="w-3.5 h-3.5 text-blue-600 rounded"
                            />
                            <span className="text-xs text-gray-800">
                              {table}
                            </span>
                          </label>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* File Upload */}
          {analysisMode === "file" && (
            <div className="mb-4">
              <label className="flex items-center justify-center gap-3 px-4 py-2 bg-blue-50 rounded-lg border border-dashed border-blue-300 cursor-pointer hover:border-blue-500 transition">
                <Upload className="w-5 h-5 text-blue-600" />
                <div className="text-center">
                  <p className="text-xs font-medium text-blue-900">
                    {files.length > 0
                      ? `${files.length} file(s) selected`
                      : "Upload files"}
                  </p>
                  <p className="text-[11px] text-blue-600">
                    XLSX, XLS, CSV, ZIP
                  </p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xls,.csv,.zip"
                  onChange={handleFileUpload}
                  multiple
                />
              </label>
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={handleAnalyze}
            disabled={!canAnalyze() || isAnalyzing}
            className={`w-full py-2.5 rounded-lg text-xs font-semibold transition flex items-center justify-center gap-2 ${
              !canAnalyze() || isAnalyzing
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {isAnalyzing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Run Analysis
              </>
            )}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <XCircle className="w-6 h-6 text-red-600" />
              <div>
                <p className="font-bold text-red-900">Analysis Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Display */}
        {analysisResults && (
          <>
            {/* File Navigation for Multiple Files */}
            {/* {multiFileResults.length > 1 && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 shadow-lg border-2 border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                      📄 Viewing File
                    </p>
                    <p className="text-xl font-black text-gray-900">
                      {analysisResults.filename ||
                        `File ${currentFileIndex + 1}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        const newIndex = currentFileIndex - 1;
                        handleChangeFile(newIndex);
                      }}
                      disabled={currentFileIndex === 0}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center gap-2 ${
                        currentFileIndex === 0
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-blue-600 text-white hover:bg-blue-700 active:scale-95"
                      }`}
                    >
                      ← Previous
                    </button>
                    <div className="px-4 py-2 bg-white rounded-lg border-2 border-blue-200">
                      <span className="text-sm font-bold text-gray-900">
                        {currentFileIndex + 1}
                      </span>
                      <span className="text-sm text-gray-500"> / </span>
                      <span className="text-sm font-bold text-gray-600">
                        {multiFileResults.length}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        const newIndex = currentFileIndex + 1;
                        handleChangeFile(newIndex);
                      }}
                      disabled={
                        currentFileIndex === multiFileResults.length - 1
                      }
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center gap-2 ${
                        currentFileIndex === multiFileResults.length - 1
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-blue-600 text-white hover:bg-blue-700 active:scale-95"
                      }`}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>
            )} */}

            {/* Metrics Grid */}
            {analysisResults.summary && (
              <MetricsCards summary={analysisResults.summary} />
            )}

            {/* ADD DIMENSION CARDS HERE */}
            {analysisResults.summary?.dimension_details && (
              <DimensionCards
                dimensions={analysisResults.summary.dimension_details}
              />
            )}

            {/* File Navigation for Multiple Files - MOVED HERE */}
{/* File Navigation for Multiple Files - MOVED HERE */}
{multiFileResults.length > 1 && (
  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 shadow-lg border-2 border-blue-100">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
          {currentFileIndex === 0 ? "📊 Combined View" : "📄 Individual File"}
        </p>
        <p className="text-xl font-black text-gray-900">
          {analysisResults.filename || `File ${currentFileIndex + 1}`}
        </p>
        {currentFileIndex === 0 && (
          <p className="text-xs text-gray-600 mt-1">
            Showing aggregated metrics from {multiFileResults.length - 1} files
          </p>
        )}
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => {
            const newIndex = currentFileIndex - 1;
            handleChangeFile(newIndex);
          }}
          disabled={currentFileIndex === 0}
          className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center gap-2 ${
            currentFileIndex === 0
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700 active:scale-95"
          }`}
        >
          ← Previous
        </button>
        <div className="px-4 py-2 bg-white rounded-lg border-2 border-blue-200">
          <span className="text-sm font-bold text-gray-900">
            {currentFileIndex === 0 ? "All" : currentFileIndex}
          </span>
          <span className="text-sm text-gray-500"> / </span>
          <span className="text-sm font-bold text-gray-600">
            {multiFileResults.length - 1}
          </span>
        </div>
        <button
          onClick={() => {
            const newIndex = currentFileIndex + 1;
            handleChangeFile(newIndex);
          }}
          disabled={currentFileIndex === multiFileResults.length - 1}
          className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center gap-2 ${
            currentFileIndex === multiFileResults.length - 1
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700 active:scale-95"
          }`}
        >
          Next →
        </button>
      </div>
    </div>
  </div>
)}

            {/* Charts */}
            {/* Dimension Distribution */}
            {analysisResults.visualizations?.issue_distribution &&
              analysisResults.visualizations.issue_distribution.length > 0 && (
                <DimensionDistribution
                  distributionData={
                    analysisResults.visualizations.issue_distribution
                  }
                />
              )}

            {/* Column-Level Analysis */}
            {analysisResults.details && analysisResults.details.length > 0 && (
              <ColumnAnalysisTable details={analysisResults.details} />
            )}

            {/* Dimension Error Analysis */}
            {/* Dimension Error Analysis */}
{/* Dimension Error Analysis - Hide only for combined view in multi-file */}
{analysisResults.summary?.dimension_details &&
  analysisResults.summary.dimension_details.length > 0 &&
  !(multiFileResults.length > 1 && currentFileIndex === 0) && (
    <DimensionErrorAnalysis
      dimensionDetails={analysisResults.summary.dimension_details}
    />
  )}

            {/* Column Statistics Heatmap */}
            {analysisResults.column_statistics &&
              analysisResults.column_statistics.length > 0 && (
                <ColumnHeatMap
                  columnStatistics={analysisResults.column_statistics}
                />
              )}

            {/* Failed Columns Analysis - New Component */}
            {analysisResults.failed_columns_analysis &&
              analysisResults.failed_columns_analysis.length > 0 &&
              analysisResults.failed_columns_analysis.some(
                (dim) => dim.columns && dim.columns.length > 0
              ) && (
                <div className="mt-8">
                  <ErrorColumnAnalysis
                    data={{
                      columns: analysisResults.failed_columns_analysis
                        .filter((dim) => dim.columns && dim.columns.length > 0)
                        .flatMap((dim) =>
                          dim.columns.map((col) => ({
                            column: col.column,
                            dimension: dim.dimension,
                            failed: col.failed,
                            total: col.total_records || col.total,
                            passed:
                              col.passed ||
                              col.total_records - col.failed ||
                              col.total - col.failed,
                          }))
                        ),
                    }}
                    errorAnalysisData={
                      multiFileResults.length > 1
                        ? analysisResults.failed_columns_analysis
                        : null
                    }
                    analysisType={
                      multiFileResults.length > 1 ? "multi_file" : "single_file"
                    }
                  />
                </div>
              )}
            {/* Detailed Column Failure Information */}
            {/* {analysisResults.dimension_details_with_fixes && 
              analysisResults.dimension_details_with_fixes.length > 0 && (
                <div className="mt-8">
                  <FailedColumnDetails 
                    failedColumnsAnalysis={analysisResults.dimension_details_with_fixes}
                  />
                </div>
              )} */}

            {/* Recommendations */}
            <Recommendations
              recommendations={analysisResults?.recommendations}
              showModal={showRecommendationsModal}
              setShowModal={setShowRecommendationsModal}
            />
          </>
        )}

        {/* Empty State */}
        {!analysisResults && !isAnalyzing && (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center bg-white p-16 rounded-3xl border-2 border-gray-200 max-w-2xl w-full shadow-xl">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 rounded-3xl flex items-center justify-center mx-auto mb-8 transform hover:scale-110 transition-transform duration-300">
                <Database className="w-12 h-12 text-blue-600" />
              </div>
              <h3 className="text-3xl font-black text-gray-900 mb-4">
                Ready to Analyze Your Data
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg mb-8">
                Configure your analysis settings and click "Run Analysis" to get
                comprehensive data quality insights
              </p>
              <div className="flex items-center justify-center gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Multi-file support</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>AI-powered insights</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Real-time analysis</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 500px;
          }
        }
        
        @keyframes popIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.5);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }

        .pie-chart-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }

        .pie-chart-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .dimension-details-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .dimension-detail-item {
          border-left: 4px solid;
          transition: all 0.3s ease;
        }

        .dimension-detail-item:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .custom-chart-tooltip {
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .custom-chart-tooltip .tooltip-label {
          margin: 0;
          font-weight: 700;
          font-size: 14px;
          margin-bottom: 8px;
        }

        /* Radial Popup Styles */
        .bg-black\/50 {
          background-color: rgba(0, 0, 0, 0.5);
        }

        @media (max-width: 768px) {
          .pie-chart-container {
            grid-template-columns: 1fr;
          }

          .radial-popup-container {
            width: 350px;
            height: 350px;
          }

          .popup-column-node {
            width: 50px;
            height: 50px;
            font-size: 9px;
          }

          .popup-center-node {
            width: 80px;
            height: 80px;
            font-size: 12px;
          }
            
        }
      `}</style>
    </div>
  );
};

export default DataQualityDashboard;
